import { payoffPlan, type DebtDto, type PayoffStrategy } from "@home/shared";
import { useMemo, useState } from "react";
import { parseAmount } from "../amount.ts";
import { compact, money } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { hapticSelect } from "../telegram.ts";
import { AmountField } from "./AmountField.tsx";
import { Segmented } from "./Segmented.tsx";

const EXTRAS = [0, 200_000, 500_000, 1_000_000];

/** Дата через n месяцев от текущего — «март 2028». */
function monthsFromNow(n: number, lang: Lang): string {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return d.toLocaleDateString(lang === "uz" ? "uz-UZ" : "ru-RU", { month: "long", year: "numeric" });
}

/** План погашения: доплата в месяц, «лавина» или «снежный ком», дата «без долгов» и экономия. */
export function PayoffPlanCard({ debts, usdRate, lang }: { debts: DebtDto[]; usdRate: number | null; lang: Lang }) {
  const [extraRaw, setExtraRaw] = useState("500000");
  const extra = parseAmount(extraRaw) ?? 0;
  const items = useMemo(
    () =>
      debts
        .filter((d) => d.status === "active" && d.remaining > 0)
        .map((d) => {
          const k = d.currency === "USD" ? (usdRate ?? 0) : 1;
          return { id: d.id, name: d.name, balance: d.remaining * k, annualRate: d.annualRate, minPayment: (d.nextPayment?.amount ?? 0) * k };
        })
        .filter((d) => d.balance > 0),
    [debts, usdRate],
  );
  const plan = useMemo(() => (items.length ? payoffPlan(items, extra) : null), [items, extra]);
  const [strategy, setStrategy] = useState<PayoffStrategy | null>(null);
  if (!plan) return null;
  const chosen = strategy ?? plan.recommended;
  const result = plan[chosen];
  const savedMonths = plan.baseline.months - result.months;
  const savedInterest = plan.baseline.totalInterest - result.totalInterest;

  return (
    <section className="dash-card">
      <h3>📈 {t("payoffTitle")}</h3>
      <p className="hint">{t("payoffIntro")}</p>
      <AmountField value={extraRaw} onChange={setExtraRaw} currency="UZS" lang={lang} label={t("payoffExtra")} />
      <div className="chips">
        {EXTRAS.map((v) => (
          <button
            key={v}
            type="button"
            className={extra === v ? "chip active" : "chip"}
            onClick={() => {
              hapticSelect();
              setExtraRaw(String(v));
            }}
          >
            {v === 0 ? t("payoffNoExtra") : `+${compact(v, lang)}`}
          </button>
        ))}
      </div>

      <Segmented
        value={chosen}
        onChange={setStrategy}
        options={[
          { value: "avalanche", label: `${t("avalanche")}${plan.recommended === "avalanche" ? " ★" : ""}` },
          { value: "snowball", label: `${t("snowball")}${plan.recommended === "snowball" ? " ★" : ""}` },
        ]}
      />
      <p className="hint">{chosen === "avalanche" ? t("avalancheHint") : t("snowballHint")}</p>

      {result.finishes ? (
        <div className="payoff-result">
          <div className="payoff-big">🎯 {t("payoffFree", { date: monthsFromNow(result.months, lang) })}</div>
          {extra > 0 && savedMonths > 0 && (
            <div className="payoff-save">{t("payoffSaved", { months: savedMonths, amount: money(Math.max(savedInterest, 0), "UZS", lang) })}</div>
          )}
          <div className="hint">{t("payoffInterest", { amount: money(result.totalInterest, "UZS", lang) })}</div>
          <ol className="payoff-order">
            {result.order.map((o) => (
              <li key={o.id}>
                <span>{o.name}</span>
                <span className="muted">{monthsFromNow(o.month, lang)}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <p className="form-error">{t("payoffNever")}</p>
      )}
    </section>
  );
}
